/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import type {AIMessagePayload} from '@adobe/exc-app/ai';
import {AIMessageType} from '@adobe/exc-app/ai';
import Emitter from '@exc/emitter';

interface StoredPromise {
  promise: Promise<AIMessagePayload>;
  reject: (err: Error) => void;
  resolve: (value: AIMessagePayload) => void;
  timeout: number;
}

type PromiseMap = Record<AIMessageType, StoredPromise>;

export const INBOUND_EVENT = 'aiMessageIncoming';
export const OUTBOUND_EVENT = 'aiMessageOutgoing';
export const PROMISE_TIMEOUT_MS = 1000;

export class AiMessenger extends Emitter {
  private promises: PromiseMap;

  constructor() {
    super();
    this.promises = {} as PromiseMap;
    document.addEventListener(INBOUND_EVENT, evt => this.handleMessage(evt));
  }

  private createPromise = (type: AIMessageType): StoredPromise => {
    const messagePromise = {} as StoredPromise;
    messagePromise.promise = new Promise<AIMessagePayload>((res, rej) => {
      messagePromise.reject = rej;
      messagePromise.resolve = res;
    });
    this.promises[type] = messagePromise;
    return messagePromise;
  };

  private handleMessage = (event: Event): void => {
    const {detail: payload} = event as CustomEvent<AIMessagePayload>;
    const {type} = payload;

    // If the type exists in the promises, resolve the promise and delete it.
    if (type in this.promises) {
      clearTimeout(this.promises[type].timeout);
      this.promises[type].resolve(payload);
      delete this.promises[type];
    }
    this.emit(type, payload);
  };

  public sendMessage = async (payload: AIMessagePayload, expectReturn?: boolean): Promise<AIMessagePayload | void> => {
    const {type} = payload;

    // If there is no expected return, dispatch the message and return undefined.
    if (!expectReturn) {
      document.dispatchEvent(new CustomEvent(OUTBOUND_EVENT, {detail: payload}));
      return undefined;
    }

    // If the promise already exists, return it.
    if (type in this.promises) {
      return this.promises[type].promise;
    }

    const messagePromise = this.createPromise(type);
    document.dispatchEvent(new CustomEvent(OUTBOUND_EVENT, {detail: payload}));

    // Promise timed out, reject and delete it.
    messagePromise.timeout = window.setTimeout(() => {
      messagePromise.reject(new Error(`${type} promise timed out`));
      delete this.promises[type];
    }, PROMISE_TIMEOUT_MS);
    return messagePromise.promise;
  };
}

export default new AiMessenger();
